import { MeasureController } from "../../MeasureController";
import { MeasureConfig } from "../../types.client";
import { parseNumericValue } from "../../utils/parseValue.utils";
import { RsrpBaseMeasure } from "../../../models/schemas/measures/radioWave.schema";

/**
 * RSRP 공통 베이스 컨트롤러
 * - SNR, MER, BER, RSSI 파싱 로직 제공
 * - 지상파, FM라디오, 위성방송 컨트롤러가 상속
 */
export abstract class RsrpBaseController<T extends RsrpBaseMeasure> extends MeasureController<T> {
  constructor(initialData: Partial<T> = {}, config: MeasureConfig = {}) {
    super(initialData, config);
  }

  /**
   * SNR 값 파싱 (dB)
   * @example "25dB" → 25
   */
  protected parseSnr(): number | null {
    return parseNumericValue(this.data.snr);
  }

  /**
   * MER 값 파싱 (dB)
   * @example "30dB" → 30
   */
  protected parseMer(): number | null {
    return parseNumericValue(this.data.mer);
  }

  /**
   * BER 값 파싱
   * @example "1e-6" → 0.000001
   */
  protected parseBer(): number | null {
    return parseNumericValue(this.data.ber);
  }

  /**
   * RSSI 값 파싱 (dBm)
   * @example "-50dBm" → -50
   */
  protected parseRssi(): number | null {
    return parseNumericValue(this.data.rssi);
  }

  /**
   * 의견 생성 - 하위 클래스에서 오버라이드 권장
   */
  protected generateDiscussion(result: "GOOD" | "CAUTION" | "BAD" | "NA"): string {
    const snr = this.parseSnr();
    const mer = this.parseMer();
    const rssi = this.parseRssi();

    if (result === "NA") {
      return "측정값이 입력되지 않았습니다.";
    }

    const parts: string[] = [];
    if (snr !== null) parts.push(`SNR: ${snr}dB`);
    if (mer !== null) parts.push(`MER: ${mer}dB`);
    if (rssi !== null) parts.push(`RSSI: ${rssi}dBm`);

    const status = result === "GOOD" ? "정상" : result === "CAUTION" ? "주의" : "불량";
    return `${parts.join(", ")} - ${status} 범위입니다.`;
  }
}
